/**
 * This file contains renders the new configureable grid layout for accounts.
 * When we've migrated everyone to grid layouts, we can merge this file back with DashboardAccountsShowView.
 */

import { RulesLogic } from 'json-logic-js';
import { useCallback } from 'react';

import { useCodeRunner } from 'dashboard/components/CodeRunner/CodeRunner';
import { GridLayoutRender } from 'dashboard/components/GridLayoutRender/GridLayoutRender';
import { AccountModule } from 'dashboard/models/account-type-grid-layouts';
import { Configuration } from 'dashboard/models/grid-layout';
import { AccountModuleBase } from 'dashboard/modules/AccountModuleBase/AccountModuleBase';
import { useAccountDataContext } from 'dashboard/routes/DashboardAccountsShowRoute/useAccountDataContext';
import { AccountApi } from 'dashboard/views/DashboardAccountsShowView/DashboardAccountsShowView';
import { runJsonLogic } from 'lib/utils/jsonLogicRunner';

interface AccountDetailsNewProps {
  configuration: Configuration<AccountModule>;
  accountApi: AccountApi;
}

export function AccountDetailsNew({ configuration, accountApi }: AccountDetailsNewProps) {
  const { account, accountVariableDefinitions } = accountApi;

  const { runCode } = useCodeRunner();
  const { dataContextPromise } = useAccountDataContext(account.id, accountVariableDefinitions);

  const handleRunCodeWithContext = useCallback(
    (code: string) => {
      return dataContextPromise.then((dataContext) => {
        return runCode(code, dataContext);
      });
    },
    [runCode, dataContextPromise],
  );

  const handleRunJsonLogicWithContext = useCallback(
    (rules: RulesLogic) => {
      return dataContextPromise.then((dataContext) => {
        return runJsonLogic(rules, dataContext);
      });
    },
    [dataContextPromise],
  );

  return (
    <GridLayoutRender
      configuration={configuration}
      renderModule={(module) => {
        return <AccountModuleBase module={module} accountApi={accountApi} />;
      }}
      runCodeWithContext={handleRunCodeWithContext}
      runJsonLogicWithContext={handleRunJsonLogicWithContext}
    />
  );
}
